import { IBody, IPage, PageNature, PageType } from '@makyoapp/makyo-sdk/models/AppBuilder'
import { BuilderDroppableElement, BuilderPage, DefaultVariableResult } from '@makyoapp/makyo-common'
import { isColorVariant } from '@makyoapp/makyo-tw-ui'

import { BuilderComponentGroup } from '../../shared/builder'
import { fromSdkActions, toSdkActions } from './BuilderActionAdapter'

export function fromSdkPageList(pages: IPage[], components: BuilderComponentGroup[]): BuilderPage[] {
  return pages.map((page) => {
    return fromSdkPage(page, components)
  })
}

export function fromSdkPage(page: IPage, components: BuilderComponentGroup[]): BuilderPage {
  return {
    id: page._id,
    tableId: page._TableID,
    isList: Boolean(page._IsList),
    title: page._Settings._Title,
    visible: page._IsVisible,
    enabled: page._Enabled,
    icon: page._Settings._Icon?.value,
    bgColor: isColorVariant(page._Settings._BackgroundColor) ? page._Settings._BackgroundColor : 'primary-95',
    tbType: Number(page._TopBar._Type),
    tbPageTitle: page._TopBar._ShowPageTitle,
    tbUseGlobalSetting: page._TopBar._UseGbSettings,
    tbLeadingElementLogOut: page._TopBar._LeadingElementLoggedOut?._Settings?.flag
      ? (page._TopBar._LeadingElementLoggedOut?._Settings as DefaultVariableResult)
      : undefined,
    tbLeadingElementLogIn: page._TopBar._LeadingElementLoggedIn?._Settings?.flag
      ? (page._TopBar._LeadingElementLoggedIn?._Settings as DefaultVariableResult)
      : undefined,
    tbTrailingElements: page._TopBar._TrailingElement?.map((te) => te._Settings as DefaultVariableResult) || [],
    bbType: Number(page._BottomBar._Type),
    bbContextualWithFab: page._BottomBar._ContextualBarSettings._DisplayFab,
    bbContextualMenu: Boolean(page._BottomBar._ContextualBarSettings._MenuID)
      ? page._BottomBar._ContextualBarSettings._MenuID
      : undefined,
    bbContextualFabIcon: page._BottomBar._ContextualBarSettings._FabIcon?.value,
    bbContextualFabActions: page._BottomBar._ContextualBarSettings._FabAction
      ? fromSdkActions(page._BottomBar._ContextualBarSettings._FabAction)?.[0]
      : undefined,
    seoTitle: page._SEO._MetaTitle,
    seoKeywords: page._SEO._MetaKeywords,
    seoDescription: page._SEO._MetaDescription,
    elements: fromSdkPageBody(page._Body, components),
    elementSettings: page._BodySettings || {},
    searchSettings: page._SearchSettings
      ? {
          table: page._SearchSettings._Table,
          columns: page._SearchSettings._Columns,
          headerColumn: page._SearchSettings._HeaderColumn,
          mediaColumn: page._SearchSettings._MediaColumn,
          contentColumn: page._SearchSettings._ContentColumn,
          metadata1Column: page._SearchSettings._Metadata1Column,
          metadata2Column: page._SearchSettings._Metadata2Column,
        }
      : undefined,
    elementSettingsTranslation: {},
    visibility: page._Behavior?._Visibility
      ? {
          // Preserve isAny from visibility object if it exists, otherwise derive from _Type
          isAny:
            page._Behavior._Visibility.isAny !== undefined
              ? page._Behavior._Visibility.isAny
              : page._Behavior._Visibility._Type !== 'all',
          conditions: page._Behavior._Visibility.conditions ?? [],
        }
      : {
          isAny: true,
          conditions: [],
        },
    actions: {
      onLoad: fromSdkActions(page._Actions),
    },
    type: Boolean(page._Settings._Type) ? page._Settings._Type : undefined,
    slug: page._Slug,
    badgeMetadata: page._BadgeMetadata as any,
    private: page._Private,
    nature: page._Nature,
    label: page._Label,
    subTitle: page._Subtitle,
    description: page._Description,
    coverImage: page._CoverImage,
    alignment: (page._Settings._Alignment ?? 'center') as any,
  }
}

export function toSdkPageList(pages: BuilderPage[]): IPage[] {
  return pages.map(toSdkPage)
}

// TODO: we can remove this interface after _BadgeMetadata officially added to IPage

export function toSdkPage(page: BuilderPage): IPage {
  return {
    _id: page.id,
    _TableID: page.tableId,
    _IsVisible: page.visible,
    _Enabled: page.enabled,
    _IsList: Boolean(page.isList),
    _GlobalSettings: {
      _HideHeader: false,
      _HideFooter: false,
    },
    _TopBar: {
      _Type: page.tbType.toString(),
      _ShowPageTitle: page.tbPageTitle,
      _LeadingElementLoggedIn: {
        _Settings: (page.tbLeadingElementLogIn as Record<string, any>) || {},
        _Actions: {},
      },
      _LeadingElementLoggedOut: {
        _Settings: (page.tbLeadingElementLogOut as Record<string, any>) || {},
        _Actions: {},
      },
      _TrailingElement: page.tbTrailingElements.map((te) => {
        return {
          _Settings: te,
          _Actions: {},
        }
      }),
      _BackgroundColor: '',
      _TextColor: '',
      _UseGbSettings: Boolean(page.tbUseGlobalSetting),
    },
    _BottomBar: {
      _Type: page.bbType.toString(),
      _ContextualBarSettings: {
        _FabAction: page.bbContextualFabActions ? toSdkActions([page.bbContextualFabActions]) : {},
        _MenuID: page.bbContextualMenu ?? '',
        _FabIcon: { value: page.bbContextualFabIcon },
        _DisplayFab: page.bbContextualWithFab,
      },
    },
    _Settings: {
      _BackgroundColor: page.bgColor,
      _Title: page.title,
      _Icon: { value: page.icon },
      _Type: page.type ?? PageType.Standard,
      _Alignment: page.alignment ?? 'center',
    },
    _Body: toSdkPageBody(page.elements),
    _BodySettings: page.elementSettings,
    _SearchSettings: page.searchSettings
      ? {
          _Table: page.searchSettings.table,
          _Columns: page.searchSettings.columns || [],
          _HeaderColumn: page.searchSettings.headerColumn,
          _ContentColumn: page.searchSettings.contentColumn,
          _MediaColumn: page.searchSettings.mediaColumn,
          _Metadata1Column: page.searchSettings.metadata1Column,
          _Metadata2Column: page.searchSettings.metadata2Column,
        }
      : undefined,
    _SEO: {
      _MetaTitle: page.seoTitle,
      _MetaDescription: page.seoDescription,
      _MetaKeywords: page.seoKeywords,
    },
    _Actions: toSdkActions(page.actions.onLoad),
    _Behavior: {
      _Metadata: {},
      _Visibility: page.visibility,
    },
    _Slug: page.slug || '',
    _BadgeMetadata: page.badgeMetadata as any,
    _Private: page.private ?? false,
    _Nature: page.nature ?? PageNature.Standard,
    _Label: page.label || '',
    _Subtitle: page.subTitle || '',
    _Description: page.description || '',
    _CoverImage: page.coverImage || {},
  }
}

export function fromSdkPageBody(
  body: IBody[],
  components: BuilderComponentGroup[],
  parentId?: string
): BuilderDroppableElement[] {
  if (!body || !Array.isArray(body)) {
    return []
  }

  const items: BuilderDroppableElement[] = []
  for (const el of body) {
    const theComponent = components
      .find((g) => g.id === el._GroupID)
      ?.components.find((c) => c.compId == el._ComponentID)

    const isTabItem = el._ComponentID === 'tab-item'
    const isNested = isTabItem || Boolean(theComponent?.nested)
    const isContainer = theComponent?.nested || isTabItem

    items.push({
      id: el._id,
      compId: el._ComponentID,
      groupId: el._GroupID,
      customTitle: el._Title || undefined,
      children: fromSdkPageBody(el._Body, components, isContainer ? el._id : undefined),
      //detailDataSource: theComponent?.detailDataSource,
      nested: isNested,
      isForm: theComponent?.isForm ?? false,
      icon: theComponent?.icon || '',
      title: isTabItem ? 'Tab' : theComponent?.title || '',
      inactive: false,
      parentId: parentId ?? null,
      parentTitle: null,
      parentType: null,
      removable: el._Removable ?? true,
      draggable: el._Draggable ?? true,
      isSubmit: el._IsSubmit ?? false,
    })
  }
  return items
}

export function toSdkPageBody(elements: BuilderDroppableElement[]): IBody[] {
  const body: IBody[] = []
  for (const el of elements) {
    body.push({
      _id: el.id,
      _ComponentID: el.compId,
      _GroupID: el.groupId,
      _Title: el.customTitle || '',
      _Body: toSdkPageBody(el.children),
      _Draggable: el.draggable ?? true,
      _Removable: el.removable ?? true,
      _IsSubmit: el.isSubmit ?? false,
      _Actions: {},
      _Behavior: {
        _Metadata: {},
        _Visibility: {
          _Type: '',
          _Clauses: [],
        },
        _UserControlledFilter: {
          _Type: '',
          _FilterBy: '',
          _ShowOnPage: false,
          _ShowOnFilterPage: false,
          _TitleIcon: { value: '' },
          _AllowMultiple: false,
        },
      },
    })
  }

  return body
}
